import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Lock, Info, RefreshCw, ClipboardCheck, ShieldAlert,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';

const STATUS_META = {
  pass: { Icon: CheckCircle2, cls: 'text-emerald-600', chip: 'border-emerald-200 bg-emerald-50 text-emerald-800', word: 'PASS' },
  fail: { Icon: XCircle, cls: 'text-rose-500', chip: 'border-rose-200 bg-rose-50 text-rose-800', word: 'FAIL' },
  locked: { Icon: Lock, cls: 'text-orange-500', chip: 'border-orange-200 bg-orange-50 text-orange-800', word: 'LOCKED' },
  enabled: { Icon: ShieldAlert, cls: 'text-amber-600', chip: 'border-amber-200 bg-amber-50 text-amber-800', word: 'ENABLED' },
  info: { Icon: Info, cls: 'text-sky-500', chip: 'border-sky-200 bg-sky-50 text-sky-800', word: 'INFO' },
};

const ItemRow = ({ item }) => {
  const meta = STATUS_META[item.status] || STATUS_META.info;
  const { Icon } = meta;
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0"
         data-testid={`preflight-item-${item.id}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.cls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-800">{item.label}</span>
          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${meta.chip}`}
                data-testid={`preflight-item-status-${item.id}`}>
            {meta.word}
          </span>
          {item.value ? (
            <span className="font-mono text-[10px] text-slate-500" data-testid={`preflight-item-value-${item.id}`}>
              {item.value}
            </span>
          ) : null}
        </div>
        {item.detail ? (
          <p className={`text-xs mt-0.5 ${item.status === 'fail' ? 'text-rose-600' : 'text-slate-500'}`}
             title={item.detail}>
            {item.detail}
          </p>
        ) : null}
        {Array.isArray(item.checks) && item.checks.length > 1 ? (
          <ul className="mt-1 space-y-0.5">
            {item.checks.map((c) => (
              <li key={c.id} className="text-[11px] text-slate-500 flex items-center gap-1.5"
                  data-testid={`preflight-subcheck-${item.id}-${c.id}`}>
                {c.ok
                  ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                  : <XCircle className="h-3 w-3 text-rose-400 shrink-0" />}
                <span className="truncate" title={`${c.label}: ${c.detail}`}>{c.label}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

/**
 * READ-ONLY production preflight checklist. Reports PASS/FAIL for every CERT
 * go-live control. It never stages, generates, transmits or changes a flag —
 * it only reads and reports. Enabling transmission remains a separate,
 * deliberate go-live action.
 */
export const ProductionPreflightPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/cert-preflight/checklist');
      setData(res.data);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail : 'Failed to load production preflight checklist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="border rounded-lg p-5 bg-white text-sm text-slate-500"
           data-testid="production-preflight-loading">
        Loading production preflight checklist…
      </div>
    );
  }
  if (!data) return null;

  const pass = data.overall === 'PASS';
  const items = data.items || [];

  return (
    <div className="border rounded-lg p-5 space-y-4 bg-white" data-testid="production-preflight-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-slate-700" />
          <h3 className="font-semibold text-slate-900">Production Preflight Checklist</h3>
          <Badge
            className={pass ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}
            data-testid="preflight-overall">
            {data.overall}
          </Badge>
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">
            read-only
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}
                data-testid="preflight-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500"
           data-testid="preflight-meta">
        <span>Environment: <span className="font-mono text-slate-700">{data.environment?.app_env}</span></span>
        <span>Transmission: <span className="font-mono text-slate-700">{data.transmission?.state}</span></span>
        {data.blocking_failures?.length > 0 ? (
          <span className="text-rose-600" data-testid="preflight-blocking">
            Blocking: {data.blocking_failures.join(', ')}
          </span>
        ) : (
          <span className="text-emerald-600">No blocking failures</span>
        )}
        {data.generated_at ? (
          <span className="ml-auto">Checked {new Date(data.generated_at).toLocaleTimeString()}</span>
        ) : null}
      </div>

      <div className="rounded-md border border-slate-200 px-3" data-testid="preflight-item-list">
        {items.map((it) => <ItemRow key={it.id} item={it} />)}
      </div>

      <p className="text-[11px] text-slate-500" data-testid="preflight-footnote">
        This panel is strictly read-only: it evaluates and reports go-live readiness.
        It never stages, generates a 270, enables transmission or changes any CERT flag.
        Every value shown is PHI-free (counts, ids, fingerprints and reason codes only).
      </p>
    </div>
  );
};

export default ProductionPreflightPanel;
